import { FieldValues, Path, useFormContext } from "react-hook-form";
import { BorrowerNotificationType, SMSGateway } from "../../OBRSettings";
import * as React from "react";
import { useLanguage } from "../../Language";
import { isFieldRequiredByNotification } from "./AdditionalEmailGroup";
import { Stack } from "@mui/material";
import { PhoneField } from "./Phone";
import { MobileCarrier } from "./MobileCarrier";
import { useWatchWithCallback } from "../../FormTextField";

export interface MobilePhoneProps<T extends FieldValues> {
  number: Path<T>;
  carrier: Path<T>;
  required?: boolean;
  hidden?: boolean;
  disabled?: boolean;
  notificationTypes: BorrowerNotificationType[][];
  smsGateways: SMSGateway[];
}

export const MobilePhone = <T extends FieldValues>({
  number,
  carrier,
  required,
  hidden,
  disabled,
  notificationTypes,
  smsGateways,
}: MobilePhoneProps<T>) => {
  const { translate } = useLanguage();
  const { trigger, getFieldState, getValues } = useFormContext<T>();
  const { isTouched: isNumberTouched } = getFieldState(number);
  const { isTouched: isCarrierTouched } = getFieldState(carrier);

  const [smsPhoneFilled, setSMSPhoneFilled] = React.useState<boolean>(
    !!getValues(number)
  );
  useWatchWithCallback(number, (value) => {
    setSMSPhoneFilled(!!value);
  });

  const isSMSRequiredByNotifications = isFieldRequiredByNotification(
    notificationTypes,
    BorrowerNotificationType.TextMessage
  );

  const smsRequired = React.useMemo(
    () =>
      required
        ? translate("isRequired", {
            LABEL: translate("ci_mobilePhone"),
          })
        : isSMSRequiredByNotifications
        ? translate("isRequiredWhenNoticeTypeIs", {
            LABEL: translate("ci_mobilePhone"),
            NOTICE_TYPE: translate("ci_notificationType_TextMessage"),
          })
        : null,
    [required, isSMSRequiredByNotifications]
  );

  React.useEffect(() => {
    if (isNumberTouched) {
      trigger(number);
    }
    if (isCarrierTouched) {
      trigger(carrier);
    }
  }, [smsRequired]);

  React.useEffect(() => {
    if (isCarrierTouched) {
      trigger(carrier);
    }
  }, [smsPhoneFilled]);

  return (
    (!hidden || !!smsRequired) && (
      <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
        <PhoneField<T>
          sx={{ flexGrow: 1 }}
          disabled={disabled}
          label={translate("ci_mobilePhone")}
          name={number}
          options={{ required: smsRequired }}
        />
        <MobileCarrier<T>
          sx={{ flexGrow: 1 }}
          disabled={disabled}
          required={smsPhoneFilled}
          smsGateways={smsGateways}
          name={carrier}
        />
      </Stack>
    )
  );
};
